'use client';

import { PageSectionsLandingHeroConversionForm } from '@/tina/__generated__/types';
import { FormActionStateContext, FormBuilder, FormManifestContext } from '../Form/FormBuilder';
import { FieldBuilder } from '../Form/Fields/FieldBuilder';
import { ConversionFormPreview } from './ConversionFormPreview';
import { use, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SteppedForm } from '../SteppedForm/SteppedForm';
import { AnimatePresence, LayoutGroup, MotionConfig } from 'motion/react';
import { makeStepId } from '../Form/utils/makeFieldId';
import type { ConversionFormPreviewMode } from './ConversionFormPreview';
export type { ConversionFormPreviewMode };
type ConversionFormProps = {
  data: PageSectionsLandingHeroConversionForm;
  previewMode?: ConversionFormPreviewMode;
};
function ConverstionFormInner({
  data,
  previewMode = 'default'
}: ConversionFormProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  /**
   * When set, the stepped dialog opens on this manifest `_key` (e.g. second
   * step after preview submit). Future: open on the meeting aside using the
   * reserved `book-meeting` slug (see `MEETING_ASIDE_SLUG` in stepKeys).
   */
  const [entryStep, setEntryStep] = useState<string | undefined>(undefined);
  const previewFormRef = useRef<HTMLDivElement>(null);
  const {
    clearErrors,
    getFieldState,
    trigger
  } = useFormContext();
  const manifest = use(FormManifestContext);
  const {
    resetActionState
  } = use(FormActionStateContext);
  const firstStep = data.form?.steps?.[0] ?? null;
  const step0Key = useMemo(() => makeStepId(firstStep?.name ?? 'step-0'), [firstStep?.name]);
  const prefetchSteps = useCallback(() => {
    void import('../SteppedForm/SteppedFormContent');
  }, []);
  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Reset success/error state. The `s` search param is left in place so
      // the user can resume the same step when reopening the dialog.
      resetActionState();
      setEntryStep(undefined);
    }
  }, [resetActionState]);
  const onSubmit = useCallback(async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const elem = previewFormRef.current;
    if (!elem) return;
    const previewVisible = window.getComputedStyle(elem).display !== 'none';
    if (previewVisible) {
      const fieldIds = manifest.fieldKeysBySection.get(step0Key) ?? [];
      const stepIndex = manifest.steps.findIndex(s => s._key === step0Key);
      const futureFieldIds = stepIndex < 0 ? [] : manifest.steps.slice(stepIndex + 1).flatMap(step => manifest.fieldKeysBySection.get(step._key) ?? []);
      const existingFutureErrorFields = new Set(futureFieldIds.filter(fieldId => getFieldState(fieldId).error));
      const ok = fieldIds.length === 0 || (await trigger(fieldIds));
      const newFutureErrorFields = futureFieldIds.filter(fieldId => !existingFutureErrorFields.has(fieldId) && getFieldState(fieldId).error);
      if (newFutureErrorFields.length) {
        clearErrors(newFutureErrorFields);
      }
      if (!ok) return;
    }
    setEntryStep(previewVisible && data.form?.steps?.[1]?.name ? makeStepId(data.form?.steps[1].name) : undefined);
    setDialogOpen(true);
  }, [clearErrors, getFieldState, manifest.fieldKeysBySection, manifest.steps, step0Key, trigger, data.form?.steps]);
  if (!firstStep) return null;
  return <MotionConfig transition={{
    type: 'spring',
    stiffness: 434.65,
    damping: 64.35,
    mass: 1.57
  }}>
      <AnimatePresence mode="popLayout">
        <LayoutGroup id="conversion-form-layout-group" key="conversion-form-layout-group">
          {!dialogOpen && <ConversionFormPreview data={data} previewMode={previewMode} previewFormRef={previewFormRef} onSubmit={onSubmit} onPrefetch={prefetchSteps}>
              <FieldBuilder step={firstStep} />
            </ConversionFormPreview>}
          <SteppedForm data={data} dialogOpen={dialogOpen} setDialogOpen={handleDialogOpenChange} initialStep={entryStep} syncStepSearchParamOnOpen onStepSearchParamCleared={() => handleDialogOpenChange(false)} />
        </LayoutGroup>
      </AnimatePresence>
    </MotionConfig>;
}
export function ConversionForm({
  data,
  previewMode = 'default'
}: ConversionFormProps) {
  return data.form && <FormBuilder formDefinition={data.form} serviceInterest={data.serviceInterest ?? undefined}>
        <ConverstionFormInner data={data} previewMode={previewMode} />
      </FormBuilder>;
}